import { IWorkflowStepState } from "../../../../@types/admin/AdminWorkflow";
import { getLongFormattedDate } from "../../../../utils/dateFormatter";
import { ErrorMessage, StatusBadge } from "../AdminPage.styles";
import {
  StepDetail,
  StepQuote,
  StepTag,
  StepTags,
  Timeline,
  TimelineBody,
  TimelineHeader,
  TimelineItem,
  TimelineTime,
  TimelineTitle,
} from "./WorkflowsPanel.styles";
import {
  describeStep,
  describeStepTiming,
  humanizeEnumName,
  isStepDone,
} from "./workflowStep";

interface IWorkflowStepTimelineProps {
  steps: IWorkflowStepState[];
  isLoading: boolean;
  isError: boolean;
}

/**
 * The executed steps of one triggered run, as a timeline (EN-5167).
 *
 * ⚠️ Design decision, and a correction to the ticket. It asks for the steps
 * "rendered as a readable tree/timeline" and calls the steps query a tree
 * walk. **It isn't one.** `getWorkflowSteps.sql` selects the step STATES of a
 * single run ordered by `Created`, LEFT JOINing the table-per-type subtype
 * tables to flatten each step's detail onto its row. The workflow
 * *definition* is a graph (`NextStepOneId` / `NextStepTwoId`), but that query
 * never touches those columns.
 *
 * So this renders what the data is: an ordered account of what happened to
 * one patient. A tree of the definition is a different (and useful) feature —
 * it belongs with the workflow authoring screens, not here.
 */
export function WorkflowStepTimeline({
  steps,
  isLoading,
  isError,
}: IWorkflowStepTimelineProps) {
  if (isError) {
    return (
      <ErrorMessage>
        Failed to load this run&apos;s steps. Go back and try again.
      </ErrorMessage>
    );
  }

  if (isLoading) return <ErrorMessage>Loading the steps...</ErrorMessage>;

  if (steps.length === 0) {
    return <ErrorMessage>This run has no recorded steps.</ErrorMessage>;
  }

  return (
    <Timeline>
      {steps.map((step) => {
        const timing = describeStepTiming(step);

        return (
          <TimelineItem key={step.id} done={isStepDone(step)}>
            <TimelineHeader>
              <TimelineTitle>{describeStep(step)}</TimelineTitle>
              <StatusBadge status={isStepDone(step) ? "active" : "warning"}>
                {humanizeEnumName(step.status)}
              </StatusBadge>
              <TimelineTime>
                {getLongFormattedDate(step.triggerDate, "-")}
              </TimelineTime>
            </TimelineHeader>

            <TimelineBody>
              {timing && <StepDetail>{timing}</StepDetail>}

              {step.measurementTypes.length > 0 && (
                <StepTags>
                  {step.measurementTypes.map((type) => (
                    <StepTag key={type}>{humanizeEnumName(type)}</StepTag>
                  ))}
                </StepTags>
              )}

              {step.followUpMessage && (
                <StepQuote>{step.followUpMessage}</StepQuote>
              )}

              {step.staffAlertMessage && (
                <StepQuote>{step.staffAlertMessage}</StepQuote>
              )}

              {step.followUpQuestion && (
                <>
                  <StepQuote>{step.followUpQuestion}</StepQuote>
                  <StepTags>
                    {[
                      step.followUpQuestionOption1,
                      step.followUpQuestionOption2,
                    ]
                      .filter((option): option is string => Boolean(option))
                      .map((option) => (
                        <StepTag
                          key={option}
                          chosen={option === step.followUpQuestionPatientAnswer}
                        >
                          {option}
                        </StepTag>
                      ))}
                  </StepTags>
                  {!step.followUpQuestionPatientAnswer && (
                    <StepDetail>Not answered</StepDetail>
                  )}
                </>
              )}

              {(step.readByPatientAt || step.answeredByPatientAt) && (
                <StepDetail>
                  {step.readByPatientAt &&
                    `Read ${getLongFormattedDate(step.readByPatientAt, "-")}`}
                  {step.readByPatientAt && step.answeredByPatientAt && " · "}
                  {step.answeredByPatientAt &&
                    `Answered ${getLongFormattedDate(
                      step.answeredByPatientAt,
                      "-"
                    )}`}
                </StepDetail>
              )}
            </TimelineBody>
          </TimelineItem>
        );
      })}
    </Timeline>
  );
}
